/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, createContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import AllReports from "./AllReports";
import Topbar from "./Topbar";
import fetchHelper from "../../services/fetchHelper";
import { downloadAsBlob } from "../../utils/helpers";
import MimeTypes from "../../constants/MimeTypes";

export const ViewTabIds = { DOWNLOAD: "download", WEEKLY: "weekly" };
export const ExportListContext = createContext({});

const ExportList = () => {
  const { tab } = useParams();
  const [selectedTab, setSelectedTab] = useState(tab || ViewTabIds.DOWNLOAD);
  const [checkedItems, setCheckedItems] = useState([]);
  const [markedAsDownloaded, setMarkedAsDownloaded] = useState([]);
  const [cancelRequest, setCancelRequest] = useState(new AbortController());
  const history = useHistory();

  const requestBatchRegenerate = async () => {
    await fetchHelper.put("/patientmanagement/rest/interrogations/batchRegenerate", {
      ids: checkedItems,
    });
  };

  const requestBatchMarkDownloaded = async () => {
    await fetchHelper.put("/patientmanagement/rest/interrogations/batchExport", {
      ids: checkedItems,
    });
    setMarkedAsDownloaded((prev) => [...prev, ...checkedItems]);
  };

  const requestBatchDownload = async () => {
    const ids = checkedItems.join(",");
    await downloadAsBlob(
      `/patientmanagement/rest/interrogations/batchDownload?ids=${ids}`,
      "download.zip",
      MimeTypes.ZIP
    );
  };

  useEffect(() => {
    // Clear checkedItems List
    setCheckedItems([]);
  }, [tab]);

  const abortRequest = () => {
    cancelRequest.abort();
    setCancelRequest(new AbortController());
  };

  const classNameTab = (pos) => {
    if (pos === selectedTab) return "tabActive";
    else return "";
  };
  return (
    <ExportListContext.Provider
      value={{ checkedItems, setCheckedItems, markedAsDownloaded, setMarkedAsDownloaded }}
    >
      <div id="index" className="containerFlex maxHd">
        <div className="contentArea">
          <Topbar
            activeTab={selectedTab}
            onBatchRegenerate={requestBatchRegenerate}
            onMarkDownloaded={requestBatchMarkDownloaded}
            onBatchDownload={requestBatchDownload}
          />

          <nav className="tabs" id="report-navigation">
            <a
              href="#"
              className={classNameTab(ViewTabIds.DOWNLOAD)}
              onClick={(e) => {
                abortRequest();
                e.preventDefault();
                setSelectedTab(ViewTabIds.DOWNLOAD);
                history.push("/billing3/export");
              }}
            >
              <span className="tab-text">Download</span>
            </a>
            <a
              href="#"
              className={classNameTab("downloaded")}
              onClick={(e) => {
                abortRequest();
                e.preventDefault();
                setSelectedTab("downloaded");
                history.push("/billing3/export/downloaded");
              }}
            >
              <span className="tab-text">Downloaded</span>
            </a>
            <a
              href="#"
              className={classNameTab("90-eligible")}
              onClick={(e) => {
                abortRequest();
                e.preventDefault();
                setSelectedTab("90-eligible");
                history.push("/billing3/export/90-eligible");
              }}
            >
              <span className="tab-text">90 Eligible</span>
            </a>
            <a
              href="#"
              className={classNameTab("90-processed")}
              onClick={(e) => {
                abortRequest();
                e.preventDefault();
                setSelectedTab("90-processed");
                history.push("/billing3/export/90-processed");
              }}
            >
              <span className="tab-text">90 Processed</span>
            </a>
            <a
              href="#"
              className={classNameTab("30-eligible")}
              onClick={(e) => {
                abortRequest();
                e.preventDefault();
                setSelectedTab("30-eligible");
                history.push("/billing3/export/30-eligible");
              }}
            >
              <span className="tab-text">30 Eligible</span>
            </a>
            <a
              href="#"
              className={classNameTab("30-processed")}
              onClick={(e) => {
                abortRequest();
                e.preventDefault();
                setSelectedTab("30-processed");
                history.push("/billing3/export/30-processed");
              }}
            >
              <span className="tab-text">30 Processed</span>
            </a>
            <a
              href="#"
              className={classNameTab("weekly")}
              onClick={(e) => {
                abortRequest();
                e.preventDefault();
                setSelectedTab("weekly");
                history.push("/billing3/export/weekly");
              }}
            >
              <span className="tab-text">Weekly</span>
            </a>
          </nav>

          <div className="tabContent">
            <AllReports activeTab={selectedTab} cancelRequest={cancelRequest} />
          </div>
        </div>
      </div>
    </ExportListContext.Provider>
  );
};

export default ExportList;
